import { useEffect, useMemo, useState } from "react";
import {
    FaArchive, FaArrowDown, FaArrowUp, FaCheck, FaClipboard, FaDownload,
    FaEdit, FaExternalLinkAlt, FaFileAlt, FaFileCsv, FaFileExcel, FaFilePdf, FaListOl,
    FaPlus, FaPrint, FaRegCircle, FaRegStar, FaStar, FaChevronDown, FaChevronUp, FaSlidersH, FaChartBar,
} from "react-icons/fa";
import { STEP_GROUPS_CONSTANTS, STEPS_CONSTANTS } from "../_MODULES/_API";
import { StepQuickAccess } from "./StepQuickAccess";
import { AppModal } from "../../../../../_CORE/APP/Templates/_UI/Modal/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { AppCodeBlock } from "../../../../../_CORE/APP/Templates/_UI/CodeBlock/_index";
import { Card, EmptyState, Skeleton, Badge } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import {
    FormGrid, FieldInput, FieldSelect, FieldTextarea, FieldCode, FieldSwitch, useForm,
} from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { useApiQuery } from "../../../../../_CORE/APP/Hooks/Hooks/_Records/_useApiQuery";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import { SearchFilter, onMatchesSearch } from "../../../../_UI/SearchFilter";
import { ViewToolbar } from "../../../../_UI/ViewToolbar";
import { StatisticsModal } from "../../../../_UI/StatisticsModal";
import { AdvancedFilterPanel } from "../../../../_UI/AdvancedFilterPanel";
import { CardActionMenu } from "../../../../_UI/CardActionMenu";
import { confirm } from "../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost";
import { onApiAction } from "../../../../_UI/onApiAction";
import { onSilentPatch } from "../../../../_UI/onSilentPatch";
import { REACT_HOT_TOAST_ERROR, REACT_HOT_TOAST_SUCCESS } from "../../../../../_CORE/HELPERS/reactHotToast.helper";
import { onExportCsv, onExportExcel, onExportPdf, onPrintTable } from "../../../../../_CORE/APP/Templates/_UI/Table/_index";

const GROUP_CATEGORIES = [
    { value: "DEPLOY", label: "Deploy" },
    { value: "SETUP", label: "Configuración / instalación" },
    { value: "MAINTENANCE", label: "Mantenimiento" },
    { value: "ROLLBACK", label: "Rollback / recuperación" },
    { value: "CHECKLIST", label: "Checklist" },
    { value: "DOCUMENTATION", label: "Documentación" },
    { value: "OTHER", label: "Otro" },
];

const STEP_TYPES = [
    { value: "ACTION", label: "Acción" },
    { value: "COMMAND", label: "Comando" },
    { value: "VERIFY", label: "Verificación" },
    { value: "NOTE", label: "Nota" },
    { value: "LINK", label: "Enlace" },
];

const LANGUAGES = [
    { value: "bash", label: "Bash" },
    { value: "powershell", label: "PowerShell" },
    { value: "cmd", label: "CMD" },
    { value: "sql", label: "SQL" },
    { value: "javascript", label: "JavaScript" },
    { value: "typescript", label: "TypeScript" },
    { value: "php", label: "PHP" },
    { value: "json", label: "JSON" },
    { value: "text", label: "Texto" },
];

const GROUP_EMPTY = {
    name: "", description: "", category: "DEPLOY", isFavorite: false,
};

const STEP_EMPTY = {
    groupId: "", title: "", type: "ACTION", description: "", command: "",
    commandLanguage: "bash", workingDirectory: "", url: "", notes: "", isDone: false,
};

const STEP_EXPORT_COLUMNS = [
    { key: "group", label: "Grupo" },
    { key: "order", label: "Orden" },
    { key: "title", label: "Paso" },
    { key: "type", label: "Tipo" },
    { key: "status", label: "Estado" },
    { key: "description", label: "Descripción" },
    { key: "workingDirectory", label: "Directorio" },
    { key: "commandLanguage", label: "Lenguaje" },
    { key: "command", label: "Comando" },
    { key: "url", label: "Enlace" },
    { key: "notes", label: "Notas / resultado esperado" },
];

const onLabel = (options, value, fallback = "") => options.find((item) => item.value === value)?.label || fallback || value;
const onAsArray = (value) => Array.isArray(value) ? value : [];
const onExportSafeName = (value) => String(value || "grupo").trim().toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "grupo";
const onStepExportRow = (step, index, groupName = "") => ({
    group: groupName, order: index + 1, title: step.title || "",
    type: onLabel(STEP_TYPES, step.type, "Acción"), status: step.isDone ? "Completado" : "Pendiente",
    description: step.description || "", workingDirectory: step.workingDirectory || "",
    commandLanguage: step.commandLanguage || "", command: step.command || "",
    url: step.url || "", notes: step.notes || "",
});

export const PanelSteps = ({ subProjectId = null, projectId = null, scopeRecord = null, projectRecord = null, onScopePatched = null }) => {
    const IS_PROJECT_SCOPE = !!projectId && !subProjectId;
    const SCOPE_ID = IS_PROJECT_SCOPE ? projectId : subProjectId;
    const SCOPE_LABEL = IS_PROJECT_SCOPE ? "proyecto" : "subproyecto";
    const [useSearch, setSearch] = useState("");
    const [useAdvancedOpen, setAdvancedOpen] = useState(false);
    const [statisticsOpen, setStatisticsOpen] = useState(false);
    const [useCategoryFilter, setCategoryFilter] = useState("ALL");
    const [useFavoriteFilter, setFavoriteFilter] = useState("ALL");
    const [useProgressFilter, setProgressFilter] = useState("ALL");
    const [useActiveGroupId, setActiveGroupId] = useState("");
    const [useGroupModal, setGroupModal] = useState(false);
    const [useGroupEdit, setGroupEdit] = useState(null);
    const [useStepModal, setStepModal] = useState(false);
    const [useStepEdit, setStepEdit] = useState(null);
    const [useGroupExportModal, setGroupExportModal] = useState(false);
    const [useExportGroupId, setExportGroupId] = useState("");
    const [useGroupExportKeys, setGroupExportKeys] = useState(STEP_EXPORT_COLUMNS.map((column) => column.key));
    const [useExpandedStepInfo, setExpandedStepInfo] = useState({});

    const {
        apiGet: onGetGroups, useApiData: GROUPS, useApiLoading: GROUPS_LOADING, mutateLocal: mutateGroups,
    } = useApiQuery({ minTimeQuery: 160, enablePagination: false, startLoading: true });

    const {
        apiGet: onGetSteps, useApiData: STEPS, useApiLoading: STEPS_LOADING, mutateLocal: mutateSteps,
    } = useApiQuery({ minTimeQuery: 160, enablePagination: false, startLoading: true });

    const { actionMutate, useActionApiIsLoading } = useApiActionMutation({
        isToastError: true, isToastSuccess: true, minTimeMutate: 160,
    });

    const onLoadGroups = () => {
        if (!SCOPE_ID) return;
        return onGetGroups({
            endpoint: IS_PROJECT_SCOPE ? STEP_GROUPS_CONSTANTS.BY_PROJECT : STEP_GROUPS_CONSTANTS.BY_SUBPROJECT,
            config: { pathParams: IS_PROJECT_SCOPE ? { projectId } : { subProjectId } },
        });
    };
    const onLoadSteps = () => {
        if (!SCOPE_ID) return;
        return onGetSteps({
            endpoint: IS_PROJECT_SCOPE ? STEPS_CONSTANTS.BY_PROJECT : STEPS_CONSTANTS.BY_SUBPROJECT,
            config: { pathParams: IS_PROJECT_SCOPE ? { projectId } : { subProjectId } },
        });
    };

    useEffect(() => {
        onLoadGroups();
        onLoadSteps();
        setActiveGroupId("");
    }, [projectId, subProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

    const GROUP_FORM = useForm({
        initialValues: GROUP_EMPTY,
        schema: { name: { required: true, minLength: 2 } },
        onSubmit: async (values) => {
            const isEdit = !!useGroupEdit?.id;
            const payload = {
                name: values.name.trim(),
                description: values.description || "",
                category: values.category || "DEPLOY",
                isFavorite: !!values.isFavorite,
            };
            if (!isEdit) payload[IS_PROJECT_SCOPE ? "projectId" : "subProjectId"] = SCOPE_ID;

            const response = isEdit
                ? await actionMutate({ method: "PATCH", endpoint: STEP_GROUPS_CONSTANTS.UPDATE, id: useGroupEdit.id, data: payload, successMessage: "Grupo actualizado" })
                : await actionMutate({ method: "POST", endpoint: STEP_GROUPS_CONSTANTS.CREATE, data: payload, successMessage: "Grupo creado" });

            if (!response.success) { GROUP_FORM.applyApiError(response); return; }
            if (isEdit) mutateGroups({ id: useGroupEdit.id, patch: response.data });
            else {
                mutateGroups({ insert: response.data });
                if (response.data?.id) setActiveGroupId(response.data.id);
            }
            setGroupEdit(null);
            setGroupModal(false);
            GROUP_FORM.reset();
        },
    });

    const STEP_FORM = useForm({
        initialValues: STEP_EMPTY,
        schema: { groupId: { required: true }, title: { required: true, minLength: 2 } },
        onSubmit: async (values) => {
            const isEdit = !!useStepEdit?.id;
            const payload = {
                groupId: values.groupId,
                title: values.title.trim(),
                type: values.type || "ACTION",
                description: values.description || "",
                command: values.command || "",
                commandLanguage: values.commandLanguage || "bash",
                workingDirectory: values.workingDirectory || "",
                url: values.url || "",
                notes: values.notes || "",
                isDone: !!values.isDone,
            };
            if (!isEdit) payload[IS_PROJECT_SCOPE ? "projectId" : "subProjectId"] = SCOPE_ID;

            const response = isEdit
                ? await actionMutate({ method: "PATCH", endpoint: STEPS_CONSTANTS.UPDATE, id: useStepEdit.id, data: payload, successMessage: "Paso actualizado" })
                : await actionMutate({ method: "POST", endpoint: STEPS_CONSTANTS.CREATE, data: payload, successMessage: "Paso creado" });

            if (!response.success) { STEP_FORM.applyApiError(response); return; }
            if (isEdit) mutateSteps({ id: useStepEdit.id, patch: response.data });
            else mutateSteps({ insert: response.data });
            setActiveGroupId(values.groupId);
            setStepEdit(null);
            setStepModal(false);
            STEP_FORM.reset();
        },
    });

    const groups = onAsArray(GROUPS);
    const steps = onAsArray(STEPS);
    const groupOptions = groups.map((group) => ({ value: group.id, label: group.name }));

    const activeGroup = groups.find((group) => group.id === useActiveGroupId) || null;
    const activeGroupSteps = activeGroup
        ? steps.filter((step) => step.groupId === activeGroup.id)
            .sort((a, b) => Number(a.position || 0) - Number(b.position || 0) || String(a.createdAt || "").localeCompare(String(b.createdAt || "")))
        : [];

    const onOpenCreateGroup = () => {
        setGroupEdit(null);
        GROUP_FORM.setValues(GROUP_EMPTY);
        setGroupModal(true);
    };

    const onOpenEditGroup = (group) => {
        setGroupEdit(group);
        GROUP_FORM.setValues({
            name: group.name ?? "", description: group.description ?? "",
            category: group.category ?? "DEPLOY", isFavorite: !!group.isFavorite,
        });
        setGroupModal(true);
    };

    const onOpenGroupWorkspace = (group) => {
        setActiveGroupId(group?.id || "");
    };

    const onCloseGroupWorkspace = () => {
        if (useStepModal || useGroupModal || useGroupExportModal) return;
        setActiveGroupId("");
    };

    const onOpenCreateStep = (groupId) => {
        if (!groupId) return;
        setStepEdit(null);
        STEP_FORM.setValues({ ...STEP_EMPTY, groupId });
        setStepModal(true);
    };

    const onOpenEditStep = (step) => {
        setStepEdit(step);
        STEP_FORM.setValues({
            groupId: step.groupId ?? "",
            title: step.title ?? "",
            type: step.type ?? "ACTION",
            description: step.description ?? "",
            command: step.command ?? "",
            commandLanguage: step.commandLanguage ?? "bash",
            workingDirectory: step.workingDirectory ?? "",
            url: step.url ?? "",
            notes: step.notes ?? "",
            isDone: !!step.isDone,
        });
        setStepModal(true);
    };

    const onToggleFavoriteGroup = async (group) => {
        const next = !group.isFavorite;
        mutateGroups({ id: group.id, patch: { isFavorite: next } });
        const response = await onSilentPatch({ endpoint: STEP_GROUPS_CONSTANTS.UPDATE, id: group.id, data: { isFavorite: next }, errorMessage: "No se pudo actualizar el destacado." });
        if (!response.success) mutateGroups({ id: group.id, patch: { isFavorite: !next } });
    };

    const onToggleDone = async (step) => {
        const next = !step.isDone;
        mutateSteps({ id: step.id, patch: { isDone: next } });
        const response = await onSilentPatch({ endpoint: STEPS_CONSTANTS.UPDATE, id: step.id, data: { isDone: next }, errorMessage: "No se pudo actualizar el paso." });
        if (!response.success) mutateSteps({ id: step.id, patch: { isDone: !next } });
    };

    const onArchiveGroup = async (group) => {
        const ok = await confirm.danger("Archivar grupo de pasos", `Se archivará "${group.name}" junto con sus pasos. Podés recuperarlo desde Archivados.`);
        if (!ok) return;
        const response = await onApiAction({ method: "PATCH", endpoint: STEP_GROUPS_CONSTANTS.ARCHIVE_ONE, pathParams: { id: group.id }, successMessage: "Grupo archivado" });
        if (response.success) {
            mutateGroups({ id: group.id, remove: true });
            steps.filter((step) => step.groupId === group.id).forEach((step) => mutateSteps({ id: step.id, remove: true }));
            if (useActiveGroupId === group.id) setActiveGroupId("");
        }
    };

    const onArchiveStep = async (step) => {
        const ok = await confirm.danger("Archivar paso", `Se archivará "${step.title}". Podés recuperarlo desde Archivados.`);
        if (!ok) return;
        const response = await onApiAction({ method: "PATCH", endpoint: STEPS_CONSTANTS.ARCHIVE_ONE, pathParams: { id: step.id }, successMessage: "Paso archivado" });
        if (response.success) mutateSteps({ id: step.id, remove: true });
    };

    const onMoveStep = async (groupSteps, step, direction) => {
        const index = groupSteps.findIndex((item) => item.id === step.id);
        const targetIndex = index + direction;
        if (index < 0 || targetIndex < 0 || targetIndex >= groupSteps.length) return;
        const other = groupSteps[targetIndex];
        const aPosition = Number.isFinite(Number(step.position)) ? Number(step.position) : index;
        const bPosition = Number.isFinite(Number(other.position)) ? Number(other.position) : targetIndex;
        mutateSteps({ id: step.id, patch: { position: bPosition } });
        mutateSteps({ id: other.id, patch: { position: aPosition } });
        const [a, b] = await Promise.all([
            onSilentPatch({ endpoint: STEPS_CONSTANTS.UPDATE, id: step.id, data: { position: bPosition }, errorMessage: "No se pudo reordenar." }),
            onSilentPatch({ endpoint: STEPS_CONSTANTS.UPDATE, id: other.id, data: { position: aPosition }, errorMessage: "No se pudo reordenar." }),
        ]);
        if (!a.success || !b.success) onLoadSteps();
    };

    const onCopy = async (text, successMessage = "Comando copiado") => {
        if (!text) {
            REACT_HOT_TOAST_ERROR("No hay contenido para copiar");
            return false;
        }
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(String(text));
            } else {
                const area = document.createElement("textarea");
                area.value = String(text);
                area.style.position = "fixed";
                area.style.left = "-9999px";
                document.body.appendChild(area);
                area.select();
                document.execCommand("copy");
                document.body.removeChild(area);
            }
            REACT_HOT_TOAST_SUCCESS(successMessage, { duration: 1800 });
            return true;
        } catch (_) {
            REACT_HOT_TOAST_ERROR("No se pudo copiar el comando");
            return false;
        }
    };

    const onCopyGroupCommands = async (group) => {
        const commandSteps = steps
            .filter((step) => step.groupId === group.id && step.command)
            .sort((a, b) => Number(a.position || 0) - Number(b.position || 0));
        if (!commandSteps.length) {
            REACT_HOT_TOAST_ERROR("Este grupo no tiene comandos para copiar");
            return;
        }
        await onCopy(
            commandSteps.map((step, index) => `# ${index + 1}. ${step.title}\n${step.command}`).join("\n\n"),
            commandSteps.length === 1 ? "Comando del grupo copiado" : `${commandSteps.length} comandos copiados`,
        );
    };

    const visibleGroups = useMemo(() => {
        const query = useSearch.trim();
        return groups
            .filter((group) => {
                const groupSteps = steps.filter((step) => step.groupId === group.id);
                if (useCategoryFilter !== "ALL" && group.category !== useCategoryFilter) return false;
                if (useFavoriteFilter !== "ALL" && (!!group.isFavorite !== (useFavoriteFilter === "YES"))) return false;
                if (useProgressFilter !== "ALL") {
                    const done = groupSteps.filter((step) => step.isDone).length;
                    if (useProgressFilter === "DONE" && (!groupSteps.length || done !== groupSteps.length)) return false;
                    if (useProgressFilter === "PENDING" && groupSteps.length > 0 && done === groupSteps.length) return false;
                    if (useProgressFilter === "EMPTY" && groupSteps.length > 0) return false;
                }
                if (!query) return true;
                return onMatchesSearch(query, [
                    group.name, group.description, onLabel(GROUP_CATEGORIES, group.category),
                    ...groupSteps.flatMap((step) => [step.title, step.description, step.command, step.workingDirectory, step.url, step.notes]),
                ]);
            })
            .sort((a, b) => (Number(!!b.isFavorite) - Number(!!a.isFavorite)) || Number(a.position || 0) - Number(b.position || 0));
    }, [groups, steps, useSearch, useCategoryFilter, useFavoriteFilter, useProgressFilter]);

    const ADVANCED_COUNT = [useCategoryFilter !== "ALL", useFavoriteFilter !== "ALL", useProgressFilter !== "ALL"].filter(Boolean).length;
    const totalDone = steps.filter((step) => step.isDone).length;
    const activeDone = activeGroupSteps.filter((step) => step.isDone).length;
    const activeProgress = activeGroupSteps.length ? Math.round((activeDone / activeGroupSteps.length) * 100) : 0;
    const activeHasCommands = activeGroupSteps.some((step) => !!step.command);
    const exportGroup = groups.find((group) => group.id === useExportGroupId) || activeGroup || null;
    const exportGroupSteps = exportGroup
        ? steps.filter((step) => step.groupId === exportGroup.id)
            .sort((a, b) => Number(a.position || 0) - Number(b.position || 0))
        : [];
    const activeExportRows = exportGroupSteps.map((step, index) => onStepExportRow(step, index, exportGroup?.name || ""));
    const activeExportColumns = STEP_EXPORT_COLUMNS.filter((column) => useGroupExportKeys.includes(column.key));

    const onOpenGroupExport = (group = activeGroup) => {
        if (!group) return;
        const groupSteps = steps.filter((step) => step.groupId === group.id);
        if (!groupSteps.length) {
            REACT_HOT_TOAST_ERROR("Este grupo no tiene pasos para exportar");
            return;
        }
        setExportGroupId(group.id);
        if (!useGroupExportKeys.length) {
            setGroupExportKeys(STEP_EXPORT_COLUMNS.map((column) => column.key));
        }
        setGroupExportModal(true);
    };

    const onDownloadMarkdown = (group, groupSteps) => {
        const lines = [`# ${group.name || "Pasos"}`, ""];
        if (group.description) lines.push(group.description, "");
        lines.push(`Categoría: ${onLabel(GROUP_CATEGORIES, group.category, "General")}`, "");
        groupSteps.forEach((step, index) => {
            lines.push(`## Paso ${index + 1}: ${step.title || "Sin título"}`, "");
            lines.push(`- Estado: ${step.isDone ? "Completado" : "Pendiente"}`);
            lines.push(`- Tipo: ${onLabel(STEP_TYPES, step.type, "Acción")}`);
            if (step.description) lines.push("", step.description);
            if (step.workingDirectory) lines.push("", `**Directorio:** \`${step.workingDirectory}\``);
            if (step.command) lines.push("", `### Comando`, "", `\`\`\`${step.commandLanguage || "text"}`, step.command, "\`\`\`");
            if (step.url) lines.push("", `**Enlace:** ${step.url}`);
            if (step.notes) lines.push("", `### Notas / resultado esperado`, "", step.notes);
            lines.push("", "---", "");
        });
        const blob = new Blob([lines.join("\n")], { type: "text/markdown;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = `pasos-${onExportSafeName(group.name)}.md`;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
    };

    const onRunGroupExport = (format) => {
        if (!exportGroup || !activeExportRows.length) {
            REACT_HOT_TOAST_ERROR("Este grupo no tiene pasos para exportar");
            return;
        }
        if (format !== "markdown" && !activeExportColumns.length) {
            REACT_HOT_TOAST_ERROR("Selecciona al menos una columna para exportar");
            return;
        }
        const baseName = `pasos-${onExportSafeName(exportGroup.name)}`;
        if (format === "csv") onExportCsv(activeExportColumns, activeExportRows, `${baseName}.csv`);
        if (format === "excel") onExportExcel(activeExportColumns, activeExportRows, `${baseName}.xlsx`, "Pasos");
        if (format === "pdf") onExportPdf(activeExportColumns, activeExportRows, `${baseName}.pdf`, exportGroup.name || "Pasos", { recordLabel: "PASO", recordLabelPlural: "pasos" });
        if (format === "print") onPrintTable(activeExportColumns, activeExportRows, exportGroup.name || "Pasos");
        if (format === "markdown") onDownloadMarkdown(exportGroup, exportGroupSteps);
        setGroupExportModal(false);
        setExportGroupId("");
    };
    const allExportRows = groups.flatMap((group) => steps
        .filter((step) => step.groupId === group.id)
        .sort((a, b) => Number(a.position || 0) - Number(b.position || 0))
        .map((step, index) => onStepExportRow(step, index, group.name || "")));
    const isLoading = GROUPS_LOADING || STEPS_LOADING;

    const onToggleStepInfo = (stepId) => {
        setExpandedStepInfo((current) => ({ ...current, [stepId]: !current[stepId] }));
    };

    const renderStepCard = (step, index, groupSteps) => {
        const secondaryInfoCount = [step.description, step.workingDirectory, step.url, step.notes].filter(Boolean).length;
        const hasSecondaryInfo = secondaryInfoCount > 0;
        const isInfoExpanded = !!useExpandedStepInfo[step.id];

        return (
            <Card key={step.id} className="LStudio__app_step_card" data-done={step.isDone ? "true" : undefined}>
                <div className="LStudio__app_step_card_top">
                    <button type="button" className="LStudio__app_step_check" onClick={() => onToggleDone(step)} aria-label={step.isDone ? "Marcar como pendiente" : "Marcar como completado"}>
                        {step.isDone ? <FaCheck /> : <FaRegCircle />}
                    </button>

                    <div className="LStudio__app_step_card_identity">
                        <div className="LStudio__app_step_card_meta">
                            <span className="LStudio__app_step_number">PASO {String(index + 1).padStart(2, "0")}</span>
                            <span className="LStudio__app_step_card_state_text">{step.isDone ? "Completado" : "Pendiente"}</span>
                            <Badge tone={step.type === "COMMAND" ? "accent" : "neutral"} size="sm">{onLabel(STEP_TYPES, step.type, "Acción")}</Badge>
                        </div>
                        <div className="LStudio__app_step_card_head">
                            <strong>{step.title}</strong>
                        </div>
                    </div>

                    <div className="LStudio__app_step_card_menu">
                        <CardActionMenu title="Acciones del paso" popoverClassName="LStudio__app_steps_workspace_popover" items={[
                            { key: "edit", label: "Editar", icon: <FaEdit />, onClick: () => onOpenEditStep(step) },
                            { key: "up", label: "Mover arriba", icon: <FaArrowUp />, isDisabled: index === 0, onClick: () => onMoveStep(groupSteps, step, -1) },
                            { key: "down", label: "Mover abajo", icon: <FaArrowDown />, isDisabled: index === groupSteps.length - 1, onClick: () => onMoveStep(groupSteps, step, 1) },
                            { divider: true },
                            { key: "archive", label: "Archivar", icon: <FaArchive />, onClick: () => onArchiveStep(step) },
                        ]} />
                    </div>
                </div>

                <div className="LStudio__app_step_card_body">
                    {step.command && (
                        <section className="LStudio__app_step_section LStudio__app_step_code LStudio__app_step_code_primary">
                            <div className="LStudio__app_step_section_head">
                                <div>
                                    <span className="LStudio__app_step_section_label">Comando / bloque técnico</span>
                                    <span className="LStudio__app_step_section_hint">{step.commandLanguage || "bash"}</span>
                                </div>
                                <AppButton size="sm" variant="ghost" tone="neutral" icon={<FaClipboard />} onClick={() => onCopy(step.command, `Comando copiado · Paso ${index + 1}`)}>Copiar comando</AppButton>
                            </div>
                            <AppCodeBlock code={step.command} language={step.commandLanguage || "bash"} isPretty={false} maxLines={14} hasCopy={false} />
                        </section>
                    )}

                    {hasSecondaryInfo && (
                        <div className="LStudio__app_step_more_row">
                            <AppButton
                                size="sm"
                                variant="ghost"
                                tone="neutral"
                                icon={isInfoExpanded ? <FaChevronUp /> : <FaChevronDown />}
                                onClick={() => onToggleStepInfo(step.id)}
                            >
                                {isInfoExpanded ? "Ocultar información" : `Ver más información (${secondaryInfoCount})`}
                            </AppButton>
                        </div>
                    )}

                    {hasSecondaryInfo && isInfoExpanded && (
                        <div className="LStudio__app_step_secondary_info">
                            {step.description && (
                                <section className="LStudio__app_step_section LStudio__app_step_description">
                                    <span className="LStudio__app_step_section_label">Descripción</span>
                                    <p>{step.description}</p>
                                </section>
                            )}

                            {(step.workingDirectory || step.url) && (
                                <div className="LStudio__app_step_meta_grid">
                                    {step.workingDirectory && (
                                        <div className="LStudio__app_step_meta_item">
                                            <span className="LStudio__app_step_section_label">Directorio de trabajo</span>
                                            <code>{step.workingDirectory}</code>
                                        </div>
                                    )}
                                    {step.url && (
                                        <div className="LStudio__app_step_meta_item">
                                            <span className="LStudio__app_step_section_label">Enlace relacionado</span>
                                            <a className="LStudio__app_step_link" href={step.url} target="_blank" rel="noreferrer"><FaExternalLinkAlt /> {step.url}</a>
                                        </div>
                                    )}
                                </div>
                            )}

                            {step.notes && (
                                <section className="LStudio__app_step_section LStudio__app_step_notes">
                                    <span className="LStudio__app_step_section_label">Notas / resultado esperado</span>
                                    <p>{step.notes}</p>
                                </section>
                            )}
                        </div>
                    )}
                </div>
            </Card>
        );
    };

    return (
        <>
            <div className="LStudio__app_panel_bar">
                <ViewToolbar
                    filters={<>
                        <SearchFilter value={useSearch} onChange={setSearch} total={groups.length} matches={visibleGroups.length} placeholder="Ej.: Deploy Frontend, npm run build o servidor..." />
                        <AppButton variant={useAdvancedOpen ? "soft" : "outline"} tone="neutral" icon={<FaSlidersH />} onClick={() => setAdvancedOpen((current) => !current)}>{ADVANCED_COUNT ? `Avanzado (${ADVANCED_COUNT})` : "Avanzado"}</AppButton>
                    </>}
                    expandedContent={<AdvancedFilterPanel isOpen={useAdvancedOpen} activeCount={ADVANCED_COUNT} onClear={() => { setCategoryFilter("ALL"); setFavoriteFilter("ALL"); setProgressFilter("ALL"); }}>
                        <FieldSelect id="steps_adv_category" label="Categoría" value={useCategoryFilter} onChange={(event) => setCategoryFilter(event.target.value)} options={[{ value: "ALL", label: "Todas las categorías" }, ...GROUP_CATEGORIES]} />
                        <FieldSelect id="steps_adv_favorite" label="Destacado" value={useFavoriteFilter} onChange={(event) => setFavoriteFilter(event.target.value)} options={[{ value: "ALL", label: "Todos" }, { value: "YES", label: "Destacados" }, { value: "NO", label: "No destacados" }]} />
                        <FieldSelect id="steps_adv_progress" label="Progreso" value={useProgressFilter} onChange={(event) => setProgressFilter(event.target.value)} options={[{ value: "ALL", label: "Todos" }, { value: "PENDING", label: "Con pasos pendientes" }, { value: "DONE", label: "Completados" }, { value: "EMPTY", label: "Sin pasos" }]} />
                    </AdvancedFilterPanel>}
                    total={visibleGroups.length}
                    totalSingular="grupo"
                    totalPlural="grupos"
                    exportColumns={STEP_EXPORT_COLUMNS}
                    exportRows={allExportRows}
                    exportName={IS_PROJECT_SCOPE ? "pasos-proyecto" : "pasos-subproyecto"}
                    actions={<>
                        <AppButton variant="outline" tone="neutral" icon={<FaChartBar />} isIconOnly title="Estadísticas" aria-label="Estadísticas" onClick={() => setStatisticsOpen(true)} />
                        <AppButton icon={<FaPlus />} onClick={onOpenCreateGroup}>Nuevo grupo</AppButton>
                    </>}
                />
            </div>

             <StepQuickAccess
                projectId={projectId}
                subProjectId={subProjectId}
                scopeRecord={scopeRecord}
                projectRecord={projectRecord}
                onScopePatched={onScopePatched}
            />

            {isLoading && <Skeleton variant="box" height={220} />}

            {!isLoading && visibleGroups.length === 0 && (
                <EmptyState icon={<FaListOl />} title={useSearch ? "No encontramos pasos" : "Todavía no hay grupos de pasos"}
                    description={useSearch ? "Probá con otro término." : `Creá, por ejemplo, Deploy Frontend, Deploy Backend, Setup o Rollback y documentá el procedimiento completo a nivel de ${SCOPE_LABEL}.`}
                    action={!useSearch ? <AppButton icon={<FaPlus />} onClick={onOpenCreateGroup}>Crear primer grupo</AppButton> : null} />
            )}

            {!isLoading && visibleGroups.length > 0 && (
                <div className="LStudio__app_steps_groups">
                    {visibleGroups.map((group) => {
                        const groupSteps = steps.filter((step) => step.groupId === group.id)
                            .sort((a, b) => Number(a.position || 0) - Number(b.position || 0));
                        const done = groupSteps.filter((step) => step.isDone).length;
                        const hasCommands = groupSteps.some((step) => !!step.command);
                        return (
                            <Card key={group.id} className="LStudio__app_steps_group_card">
                                <div className="LStudio__app_steps_group_card_top">
                                    <span className="LStudio__app_steps_group_icon"><FaListOl /></span>
                                    <div className="LStudio__app_steps_group_text">
                                        <div className="LStudio__app_steps_group_titleline">
                                            <strong>{group.name}</strong>
                                            {group.isFavorite && <FaStar aria-label="Destacado" />}
                                            <Badge tone="neutral" size="sm">{onLabel(GROUP_CATEGORIES, group.category, "General")}</Badge>
                                        </div>
                                        {group.description && <span className="LStudio__app_steps_group_desc">{group.description}</span>}
                                    </div>
                                    <CardActionMenu title="Acciones del grupo" items={[
                                        { key: "favorite", label: group.isFavorite ? "Quitar destacado" : "Destacar", icon: group.isFavorite ? <FaStar /> : <FaRegStar />, onClick: () => onToggleFavoriteGroup(group) },
                                        ...(hasCommands ? [{ key: "copy", label: "Copiar todos los comandos", icon: <FaClipboard />, onClick: () => onCopyGroupCommands(group) }] : []),
                                        { key: "export", label: "Descargar / exportar...", icon: <FaDownload />, onClick: () => onOpenGroupExport(group) },
                                        { key: "edit", label: "Editar grupo", icon: <FaEdit />, onClick: () => onOpenEditGroup(group) },
                                        { divider: true },
                                        { key: "archive", label: "Archivar", icon: <FaArchive />, onClick: () => onArchiveGroup(group) },
                                    ]} />
                                </div>

                                <div className="LStudio__app_steps_group_card_stats">
                                    <div><strong>{groupSteps.length}</strong><span>pasos</span></div>
                                    <div><strong>{done}</strong><span>completados</span></div>
                                    <div><strong>{Math.max(groupSteps.length - done, 0)}</strong><span>pendientes</span></div>
                                </div>

                                <div className="LStudio__app_steps_group_card_footer">
                                    <span className="LStudio__app_steps_group_progress">{done} / {groupSteps.length}</span>
                                    <AppButton size="sm" variant="soft" tone="neutral" onClick={() => onOpenGroupWorkspace(group)}>Abrir pasos</AppButton>
                                </div>
                            </Card>
                        );
                    })}
                </div>
            )}

            <StatisticsModal isOpen={statisticsOpen} onClose={() => setStatisticsOpen(false)}
                title="Estadísticas · Pasos" description={`Resumen de los grupos y pasos de este ${SCOPE_LABEL}.`}
                metrics={[
                    { key: "groups", label: "Grupos", value: groups.length, icon: <FaListOl />, tone: "neutral", isLoading },
                    { key: "steps", label: "Pasos", value: steps.length, icon: <FaFileAlt />, tone: "accent", isLoading },
                    { key: "done", label: "Completados", value: totalDone, icon: <FaCheck />, tone: "ok", isLoading },
                    { key: "pending", label: "Pendientes", value: Math.max(steps.length - totalDone, 0), icon: <FaRegCircle />, tone: "warn", isLoading },
                ]} />

            <AppModal
                isOpen={!!activeGroup}
                onClose={onCloseGroupWorkspace}
                size="full"
                className="LStudio__app_steps_workspace_modal"
                title={activeGroup ? `PASOS · ${activeGroup.name}` : "Pasos"}
                description="Los pasos viven dentro de este espacio. Podés agregarlos, completarlos, editarlos, reordenarlos y copiar comandos sin salir del grupo."
                footer={<AppButton variant="ghost" tone="neutral" onClick={() => setActiveGroupId("")}>Cerrar</AppButton>}
            >
                {activeGroup && (
                    <div className="LStudio__app_steps_workspace">
                        <StepQuickAccess
                            projectId={projectId}
                            subProjectId={subProjectId}
                            scopeRecord={scopeRecord}
                            projectRecord={projectRecord}
                            onScopePatched={onScopePatched}
                            variant="workspace"
                        />

                        <Card className="LStudio__app_steps_workspace_parent">
                            <div className="LStudio__app_steps_workspace_parent_main">
                                <span className="LStudio__app_steps_group_icon"><FaListOl /></span>
                                <div className="LStudio__app_steps_workspace_parent_copy">
                                    <div className="LStudio__app_steps_group_titleline">
                                        <strong>{activeGroup.name}</strong>
                                        {activeGroup.isFavorite && <FaStar aria-label="Destacado" />}
                                        <Badge tone="neutral" size="sm">{onLabel(GROUP_CATEGORIES, activeGroup.category, "General")}</Badge>
                                    </div>
                                    <span className="LStudio__app_steps_group_desc">{activeGroup.description || `Grupo de pasos del ${SCOPE_LABEL}.`}</span>
                                </div>
                            </div>

                            <div className="LStudio__app_steps_workspace_progress">
                                <div className="LStudio__app_steps_workspace_progress_top">
                                    <span>Progreso</span>
                                    <strong>{activeDone} / {activeGroupSteps.length}</strong>
                                </div>
                                <div className="LStudio__app_steps_workspace_progress_track" aria-label={`${activeProgress}% completado`}>
                                    <span style={{ width: `${activeProgress}%` }} />
                                </div>
                                <small>{activeProgress}% completado</small>
                            </div>

                            <div className="LStudio__app_steps_workspace_actions">
                                <AppButton size="sm" icon={<FaPlus />} onClick={() => onOpenCreateStep(activeGroup.id)}>Agregar paso</AppButton>
                                <CardActionMenu title="Acciones del grupo" popoverClassName="LStudio__app_steps_workspace_popover" items={[
                                    { key: "favorite", label: activeGroup.isFavorite ? "Quitar destacado" : "Destacar", icon: activeGroup.isFavorite ? <FaStar /> : <FaRegStar />, onClick: () => onToggleFavoriteGroup(activeGroup) },
                                    { key: "copy", label: "Copiar comandos", icon: <FaClipboard />, isDisabled: !activeHasCommands, onClick: () => onCopyGroupCommands(activeGroup) },
                                    { key: "export", label: "Descargar / exportar...", icon: <FaDownload />, onClick: () => onOpenGroupExport(activeGroup) },
                                    { key: "edit", label: "Editar grupo", icon: <FaEdit />, onClick: () => onOpenEditGroup(activeGroup) },
                                    { divider: true },
                                    { key: "archive", label: "Archivar", icon: <FaArchive />, onClick: () => onArchiveGroup(activeGroup) },
                                ]} />
                            </div>
                        </Card>

                        {activeGroupSteps.length === 0 ? (
                            <EmptyState
                                icon={<FaListOl />}
                                title="Este grupo todavía no tiene pasos"
                                description="Agregá el primer paso y construí el procedimiento dentro de este mismo espacio."
                                action={<AppButton icon={<FaPlus />} onClick={() => onOpenCreateStep(activeGroup.id)}>Agregar primer paso</AppButton>}
                            />
                        ) : (
                            <div className="LStudio__app_steps_workspace_grid">
                                {activeGroupSteps.map((step, index) => renderStepCard(step, index, activeGroupSteps))}
                            </div>
                        )}
                    </div>
                )}
            </AppModal>

            <AppModal
                isOpen={useGroupExportModal}
                onClose={() => { setGroupExportModal(false); setExportGroupId(""); }}
                size="md"
                title="Guardar / exportar pasos"
                description="Elegí qué columnas querés incluir y luego el formato de salida."
                footer={<AppButton variant="ghost" tone="neutral" onClick={() => setGroupExportModal(false)}>Cerrar</AppButton>}
            >
                <div className="LStudio__app_steps_export_modal">
                    <div className="LStudio__app_steps_export_columns_head">
                        <div>
                            <strong>Columnas</strong>
                            <span>{useGroupExportKeys.length} de {STEP_EXPORT_COLUMNS.length} seleccionadas</span>
                        </div>
                        <div>
                            <AppButton size="sm" variant="ghost" tone="neutral" onClick={() => setGroupExportKeys(STEP_EXPORT_COLUMNS.map((column) => column.key))}>Seleccionar todo</AppButton>
                            <AppButton size="sm" variant="ghost" tone="neutral" onClick={() => setGroupExportKeys([])}>Ninguna</AppButton>
                        </div>
                    </div>

                    <div className="LStudio__app_steps_export_columns">
                        {STEP_EXPORT_COLUMNS.map((column) => (
                            <label key={column.key} className="LStudio__app_steps_export_column">
                                <input
                                    type="checkbox"
                                    checked={useGroupExportKeys.includes(column.key)}
                                    onChange={(event) => setGroupExportKeys((current) => event.target.checked
                                        ? [...new Set([...current, column.key])]
                                        : current.filter((key) => key !== column.key))}
                                />
                                <span>{column.label}</span>
                            </label>
                        ))}
                    </div>

                    <div className="LStudio__app_steps_export_formats">
                        <span className="LStudio__app_steps_export_formats_label">Formato de salida</span>
                        <div>
                            <AppButton variant="outline" tone="neutral" icon={<FaFileCsv />} isDisabled={!activeExportColumns.length} onClick={() => onRunGroupExport("csv")}>CSV</AppButton>
                            <AppButton variant="outline" tone="neutral" icon={<FaFileExcel />} isDisabled={!activeExportColumns.length} onClick={() => onRunGroupExport("excel")}>Excel</AppButton>
                            <AppButton variant="outline" tone="neutral" icon={<FaFilePdf />} isDisabled={!activeExportColumns.length} onClick={() => onRunGroupExport("pdf")}>PDF</AppButton>
                            <AppButton variant="outline" tone="neutral" icon={<FaPrint />} isDisabled={!activeExportColumns.length} onClick={() => onRunGroupExport("print")}>Imprimir</AppButton>
                            <AppButton variant="outline" tone="neutral" icon={<FaFileAlt />} isDisabled={!exportGroupSteps.length} onClick={() => onRunGroupExport("markdown")}>Markdown</AppButton>
                        </div>
                    </div>
                </div>
            </AppModal>

            <AppModal isOpen={useGroupModal} onClose={() => { setGroupModal(false); setGroupEdit(null); }} isDirty={GROUP_FORM.isDirty}
                size="lg" title={useGroupEdit ? "Editar grupo de pasos" : "Nuevo grupo de pasos"}
                description="Agrupá un procedimiento completo, por ejemplo Deploy Frontend, Deploy Backend, Rollback o Configuración inicial."
                footer={<><AppButton variant="ghost" tone="neutral" onClick={() => setGroupModal(false)}>Cancelar</AppButton><AppButton onClick={GROUP_FORM.onSubmit} isLoading={useActionApiIsLoading}>{useGroupEdit ? "Guardar cambios" : "Crear grupo"}</AppButton></>}>
                <div className="LStudio__app_priority_toggle LStudio__app_steps_form_priority">
                    <FieldSwitch id="step_group_favorite" label="Destacado" text="Mostrar este grupo primero" isChecked={!!GROUP_FORM.values.isFavorite} onChange={(event) => GROUP_FORM.setValue("isFavorite", event.target.checked)} />
                </div>
                <FormGrid columns={2}>
                    <FieldInput {...GROUP_FORM.field("name")} label="Nombre del grupo" placeholder="Ej: Deploy Frontend" />
                    <FieldSelect {...GROUP_FORM.field("category")} label="Categoría" options={GROUP_CATEGORIES} />
                    <FieldTextarea {...GROUP_FORM.field("description")} label="Descripción" rows={4} span={2} placeholder="Qué resuelve este procedimiento, cuándo usarlo y qué resultado se espera." />
                </FormGrid>
            </AppModal>

            <AppModal isOpen={useStepModal} onClose={() => { setStepModal(false); setStepEdit(null); }} isDirty={STEP_FORM.isDirty}
                size="xl" title={useStepEdit ? "Editar paso" : "Nuevo paso"}
                description="El paso puede ser una explicación, un comando, una verificación, una nota o una combinación de varios campos."
                footer={<><AppButton variant="ghost" tone="neutral" onClick={() => setStepModal(false)}>Cancelar</AppButton><AppButton onClick={STEP_FORM.onSubmit} isLoading={useActionApiIsLoading}>{useStepEdit ? "Guardar cambios" : "Guardar paso"}</AppButton></>}>
                <div className="LStudio__app_priority_toggle LStudio__app_steps_form_priority">
                    <FieldSwitch id="step_is_done" label="Completado" text="Marcar este paso como realizado" isChecked={!!STEP_FORM.values.isDone} onChange={(event) => STEP_FORM.setValue("isDone", event.target.checked)} />
                </div>
                <FormGrid columns={2}>
                    <FieldSelect {...STEP_FORM.field("groupId")} label="Grupo" options={groupOptions} isDisabled />
                    <FieldSelect {...STEP_FORM.field("type")} label="Tipo de paso" options={STEP_TYPES} />
                    <FieldInput {...STEP_FORM.field("title")} label="Título" placeholder="Ej: Compilar frontend" span={2} />
                    <FieldTextarea {...STEP_FORM.field("description")} label="Descripción" rows={4} span={2} placeholder="Explicá qué hay que hacer, por qué y qué tener en cuenta." />
                    <FieldInput {...STEP_FORM.field("workingDirectory")} label="Directorio de trabajo" placeholder="Ej: /var/www/app/frontend" />
                    <FieldSelect {...STEP_FORM.field("commandLanguage")} label="Lenguaje del comando" options={LANGUAGES} />
                    <FieldCode {...STEP_FORM.field("command")} label="Comando / bloque técnico" language={STEP_FORM.values.commandLanguage || "bash"} span={2} placeholder="Ej: npm ci && npm run build" />
                    <FieldInput {...STEP_FORM.field("url")} label="Enlace relacionado" placeholder="Ej: https://..." span={2} />
                    <FieldTextarea {...STEP_FORM.field("notes")} label="Notas / resultado esperado" rows={4} span={2} placeholder="Ej: Verificar que dist/ se haya generado y que no existan errores." />
                </FormGrid>
            </AppModal>
        </>
    );
};
